// src/components/reports/ErrorsBySourceEntity.js
// Unified component for Integration Errors AND EDS Kickouts by source/entity.
// Pass type="integration" (from /IntegrationErrors route) or type="kickout" (from /KickoutErrors).
//
// Differences by type:
//   integration → API: errorlog/source/.../entity/...  dateField: ErrorTs  idField: pkError  download: DownloadErrorEventFile  updatePath: eds/error/update/pkError/{id}/status/{s}
//   kickout     → API: kickouts/source/.../entity/...  dateField: InsertDate  idField: EventId  download: DownloadEventKickoutFile  updatePath: eds/kickout/update/eventID/{id}/status/{s}

import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { IconButton, Tooltip } from "@mui/material";
import { CheckCircle, HourglassTop, ErrorOutline } from "@mui/icons-material";
import { MaterialReactTable } from "material-react-table";
import { sharedTableProps } from "../shared/tableProps";
import BackToolbarLink from "../shared/BackToolbarLink";
import "../util/tables.css";
import * as API from "../api/index";
import { ROUTES } from "../../api/routes";
import ShowLoadingBar from "../util/showLoadingBar";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { NotificationManager } from "react-notifications";

dayjs.extend(utc);

// ─── Config per type ────────────────────────────────────────────────────────

const TYPE_CONFIG = {
  integration: {
    title: "Integration Errors",
    getResourcePath: (source, entity) => ROUTES.errorLogBySourceEntity(source, entity),
    dateField: "ErrorTs",
    idField: "pkError",
    idHeader: "Event Id",
    descField: "ErrorDesc",
    descHeader: "Error Description",
    extraColumns: [
      { accessorKey: "ServiceName",  header: "Service Name",  size: 40 },
      { accessorKey: "ServicePath",  header: "Service Path",  size: 40 },
      { accessorKey: "ErrorCode",    header: "Error Code",    size: 40 },
    ],
    timestampHeader: "Error Timestamp",
    downloadFn: (id) => API.DownloadErrorEventFile(id),
    updatePath: (id, status) => `integrations/eds/error/update/pkError/${id}/status/${status}`,
    notificationEntity: "Integration Errors",
  },
  kickout: {
    title: "Kickout Errors",
    getResourcePath: (source, entity) => ROUTES.kickouts.bySourceEntity(source, entity),
    dateField: "InsertDate",
    idField: "EventId",
    idHeader: "Event Id",
    descField: "ErrorMsg",
    descHeader: "Error Message",
    extraColumns: [
      { accessorKey: "StoredProcedure", header: "Stored Procedure", size: 40 },
    ],
    timestampHeader: "Insert Date",
    downloadFn: (id) => API.DownloadEventKickoutFile(id),
    updatePath: (id, status) => `integrations/eds/kickout/update/eventID/${id}/status/${status}`,
    notificationEntity: "Kickout",
  },
};

// ─── Helpers ────────────────────────────────────────────────────────────────

const PERIOD_LABELS = {
  "7D": "Last 7 Days",
  Yesterday: "Yesterday",
  Today: "Today",
  "30D": "Last 30 Days",
};

function isBetweenInclusive(d, start, end) {
  return (d.isAfter(start) || d.isSame(start)) && (d.isBefore(end) || d.isSame(end));
}

function filterByPeriod(data, period, dateField) {
  const today = dayjs.utc().startOf("day");
  const ranges = {
    "30D":      { start: today.subtract(30, "day"), end: today.subtract(1, "day").endOf("day") },
    "7D":       { start: today.subtract(7,  "day"), end: today.subtract(1, "day").endOf("day") },
    Yesterday:  { start: today.subtract(1, "day"),  end: today.subtract(1, "day").endOf("day") },
    Today:      { start: today,                      end: today.endOf("day") },
  };
  const range = ranges[period];
  if (!range) return data;
  return data.filter((r) => isBetweenInclusive(dayjs.utc(r[dateField]), range.start, range.end));
}

function normalizeStatus(r) {
  const s = r.record_status?.toUpperCase();
  return {
    ...r,
    record_status: s === "INPROGRESS" ? "INPROGRESS" : s === "RESOLVED" ? "RESOLVED" : "UNRESOLVED",
  };
}

const STATUS_COLORS = { RESOLVED: "#2e7d32", INPROGRESS: "#f57c00", UNRESOLVED: "#d32f2f" };

async function triggerDownload(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

// ─── Component ───────────────────────────────────────────────────────────────

const ErrorsBySourceEntity = ({ type }) => {
  const cfg = TYPE_CONFIG[type];
  const [errorList, setErrorList] = useState([]);
  const [rowSelection, setRowSelection] = useState({});
  const [selectedRows, setSelectedRows] = useState([]);
  const { Source, Entity, Period } = useParams();
  const navigate = useNavigate();

  const periodText = PERIOD_LABELS[Period] ?? "Last 30 Days";
  const resourcePath = cfg.getResourcePath(Source, Entity);

  useEffect(() => {
    API.GetDataFromAPI(resourcePath).then((data) => {
      let filtered = filterByPeriod(data, Period, cfg.dateField);
      if (!filtered.length) filtered = data;
      setErrorList(filtered.map(normalizeStatus));
    });
  }, [resourcePath, Period, cfg.dateField]);

  const handleDownload = async (id) => {
    try {
      const blob = await cfg.downloadFn(id);
      await triggerDownload(blob, `${id}.xml`);
    } catch (err) {
      console.error("Download error:", err);
      alert("File download failed");
    }
  };

  const updateStatusBulk = async (newStatus) => {
    if (!selectedRows.length) return;

    const results = await Promise.allSettled(
      selectedRows.map((row) =>
        API.UpdateRecordByPath(cfg.updatePath(row[cfg.idField], newStatus)),
      ),
    );

    const succeeded = [];
    const failed = [];
    results.forEach((res, idx) => {
      if (res.status === "fulfilled" && res.value?.code === 200)
        succeeded.push(selectedRows[idx][cfg.idField]);
      else failed.push(selectedRows[idx][cfg.idField]);
    });

    if (succeeded.length)
      NotificationManager.success(
        `Updated ${succeeded.length} record(s) to ${newStatus}`,
        cfg.notificationEntity,
        5000,
      );
    if (failed.length)
      NotificationManager.error(
        `Failed for: ${failed.join(", ")}`,
        cfg.notificationEntity,
        5000,
      );

    setErrorList((prev) =>
      prev.map((row) =>
        succeeded.includes(row[cfg.idField]) ? { ...row, record_status: newStatus } : row,
      ),
    );
    setRowSelection({});
    setSelectedRows([]);
  };

  const columns = [
    {
      accessorKey: cfg.idField,
      header: cfg.idHeader,
      size: 30,
      Cell: ({ row }) => (
        // eslint-disable-next-line jsx-a11y/anchor-is-valid
        <a
          href="#"
          onClick={() => handleDownload(row.original[cfg.idField])}
          style={{ textDecoration: "underline", color: "#1976d2" }}
        >
          {row.original[cfg.idField]}
        </a>
      ),
    },
    {
      accessorKey: cfg.descField,
      header: cfg.descHeader,
      size: 200,
      Cell: ({ cell }) => (
        <div style={{ whiteSpace: "normal", wordBreak: "break-word", maxWidth: 400 }}>
          {cell.getValue()}
        </div>
      ),
    },
    { accessorKey: cfg.dateField, header: cfg.timestampHeader, size: 40 },
    ...cfg.extraColumns,
    {
      accessorKey: "record_status",
      header: "Status",
      size: 40,
      Cell: ({ row }) => {
        const val = row.original.record_status || "UNRESOLVED";
        return (
          <span style={{ fontWeight: "bold", color: STATUS_COLORS[val] || "#000", display: "block", textAlign: "center" }}>
            {val}
          </span>
        );
      },
    },
  ];

  const loading = errorList.length === 0;
  if (loading) return <ShowLoadingBar title={cfg.title} loading />;

  const hasSelection = selectedRows.length > 0;
  const iconStyle = {
    backgroundColor: hasSelection ? "#e0f7fa" : "transparent",
    borderRadius: "50%",
    boxShadow: hasSelection ? "0 0 6px rgba(0,0,0,0.3)" : "none",
  };

  return (
    <div className="relative">
      <h2 style={{ textAlign: "center", color: "#0b4d8fff", paddingLeft: "20px" }}>
        {cfg.title}
      </h2>

      <MaterialReactTable
        {...sharedTableProps}
        columns={columns}
        data={errorList}
        initialState={{ density: "compact", pagination: { pageSize: 20 } }}
        enableRowSelection
        getRowId={(row) => String(row[cfg.idField])}
        onRowSelectionChange={(updater) => {
          const newSel = typeof updater === "function" ? updater(rowSelection) : updater;
          setRowSelection(newSel);
          const ids = Object.keys(newSel).filter((k) => newSel[k]);
          setSelectedRows(errorList.filter((r) => ids.includes(String(r[cfg.idField]))));
        }}
        state={{ rowSelection }}
        muiTableHeadCellProps={{
          sx: { "& .MuiTableHeadCell-Content": { justifyContent: "center" }, textAlign: "center" },
        }}
        muiTableBodyCellProps={{
          sx: { whiteSpace: "normal", wordBreak: "break-word" },
        }}
        renderTopToolbarCustomActions={() => (
          <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", width: "100%", fontWeight: "bold", fontSize: "0.875rem", padding: "4px 0" }}>
            <BackToolbarLink onClick={() => navigate(-1)} />
            <span style={{ marginRight: "24px" }}>Source = <span style={{ color: "#2e7d32" }}>{Source}</span></span>
            <span style={{ marginRight: "24px" }}>Entity = <span style={{ color: "#2e7d32" }}>{Entity}</span></span>
            <span style={{ marginRight: "24px" }}>Period = <span style={{ color: "#2e7d32" }}>{periodText}</span></span>

            <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8, paddingRight: 16 }}>
              <Tooltip title="Mark as Resolved">
                <span>
                  <IconButton onClick={() => updateStatusBulk("RESOLVED")} disabled={!hasSelection} sx={{ color: hasSelection ? "#2e7d32" : "grey", ...iconStyle }}>
                    <CheckCircle />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Mark as In Progress">
                <span>
                  <IconButton onClick={() => updateStatusBulk("INPROGRESS")} disabled={!hasSelection} sx={{ color: hasSelection ? "#f57c00" : "grey", ...iconStyle }}>
                    <HourglassTop />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Mark as Unresolved">
                <span>
                  <IconButton onClick={() => updateStatusBulk("UNRESOLVED")} disabled={!hasSelection} sx={{ color: hasSelection ? "#d32f2f" : "grey", ...iconStyle }}>
                    <ErrorOutline />
                  </IconButton>
                </span>
              </Tooltip>
            </div>
          </div>
        )}
      />
    </div>
  );
};

export default ErrorsBySourceEntity;
